import { httpsCallable } from "firebase/functions";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { getDb, getFirebaseAuth } from "../firebase/config";
import { getFunctionsAsia } from "../firebase/functions";
import {
  CHECKOUT_SITE,
  RAZORPAY_KEY_ID,
  SHOP_PRODUCTS,
  isNativeShell,
  loadRazorpayScript,
  type ShopProductId,
} from "./catalog";
import {
  detectPriceRegion,
  getFullCourseRegionalPrice,
  type PriceRegionId,
} from "./regionalPricing";

export type BuyResult = { ok: boolean; message: string };

function chargeForProduct(productId: ShopProductId, regionId?: PriceRegionId): {
  chargeAmountInr: number;
  regionId: PriceRegionId;
} {
  if (productId === "full_course") {
    const p = getFullCourseRegionalPrice(regionId);
    return { chargeAmountInr: p.chargeAmountInr, regionId: p.regionId };
  }
  const product = SHOP_PRODUCTS[productId];
  return { chargeAmountInr: product.amountInr, regionId: detectPriceRegion() };
}

async function ensureGoogleUid(): Promise<string> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error("Firebase not configured.");
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error("Please sign in with your Google account before purchasing.");
  }
  return user.uid;
}

async function confirmOnServer(paymentId: string, productId: ShopProductId): Promise<void> {
  const fn = httpsCallable(getFunctionsAsia(), "confirmRazorpayPayment");
  await fn({ paymentId, productId });
}

/**
 * Razorpay Checkout.js — only on the public website (policy-safe host).
 */
export async function buyProductInApp(
  productId: ShopProductId,
  onToast: (msg: string) => void,
): Promise<BuyResult> {
  const product = SHOP_PRODUCTS[productId];
  if (!product) return { ok: false, message: "Unknown product." };
  if (!product.available) return { ok: false, message: "This pack is coming soon." };

  const uid = await ensureGoogleUid();
  const auth = getFirebaseAuth()!;
  const user = auth.currentUser!;
  const regionId = detectPriceRegion();
  const { chargeAmountInr } = chargeForProduct(productId, regionId);

  await loadRazorpayScript();
  if (!window.Razorpay) return { ok: false, message: "Razorpay failed to load." };

  return new Promise((resolve) => {
    const rzp = new window.Razorpay!({
      key: RAZORPAY_KEY_ID,
      amount: chargeAmountInr * 100,
      currency: "INR",
      name: "Ultimate Piano Tutor",
      description: product.title,
      // Versioned URL prevents Razorpay/CDN from reusing the previous brand image.
      image: `${CHECKOUT_SITE}/logo.webp?v=20260719`,
      prefill: {
        name: user.displayName || "Player",
        email: user.email || "",
      },
      notes: {
        uid,
        productId,
        regionId,
        app: "ultimate-piano-tutor",
      },
      theme: { color: "#3eb6ff" },
      handler: async (response: { razorpay_payment_id: string }) => {
        try {
          onToast("Payment received — unlocking…");
          await confirmOnServer(response.razorpay_payment_id, productId);
          resolve({ ok: true, message: `${product.title} unlocked!` });
        } catch (e) {
          const msg =
            e instanceof Error
              ? e.message
              : "Payment OK — unlock pending (webhook). Re-open app in a minute.";
          resolve({ ok: false, message: msg });
        }
      },
      modal: {
        ondismiss: () => resolve({ ok: false, message: "Checkout closed." }),
      },
    });
    rzp.open();
  });
}

/**
 * Desktop / Android path: open hosted website checkout tied to this Firebase uid.
 */
export async function buyProductViaWebsite(productId: ShopProductId): Promise<BuyResult> {
  const product = SHOP_PRODUCTS[productId];
  if (!product?.available) return { ok: false, message: "This pack is coming soon." };
  const uid = await ensureGoogleUid();
  const db = getDb();
  if (!db) return { ok: false, message: "Firebase not configured." };

  const regionId = detectPriceRegion();
  const { chargeAmountInr } = chargeForProduct(productId, regionId);

  const sessionId = `${uid}_${productId}_${Date.now()}`;
  await setDoc(doc(db, "checkoutSessions", sessionId), {
    uid,
    productId,
    amountInr: chargeAmountInr,
    regionId,
    status: "open",
    createdAt: serverTimestamp(),
    app: "ultimate-piano-tutor",
  });

  const url = `${CHECKOUT_SITE}/?buy=${encodeURIComponent(productId)}&session=${encodeURIComponent(sessionId)}`;
  try {
    if (typeof window.Capacitor?.isNativePlatform === "function" && window.Capacitor.isNativePlatform()) {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
  return {
    ok: true,
    message: "Checkout opened in browser. After pay, return here — unlock syncs automatically.",
  };
}

export async function startPurchase(
  productId: ShopProductId,
  onToast: (msg: string) => void,
): Promise<BuyResult> {
  // Electron + Capacitor: always website (Razorpay / store policy).
  if (isNativeShell()) {
    return buyProductViaWebsite(productId);
  }
  try {
    return await buyProductInApp(productId, onToast);
  } catch {
    onToast("Opening website checkout…");
    return buyProductViaWebsite(productId);
  }
}

/** Auto-open Razorpay when landing on the website from ?buy=&session= */
export async function maybeResumeWebsiteCheckout(onToast: (msg: string) => void): Promise<void> {
  if (isNativeShell()) return;

  const params = new URLSearchParams(location.search);
  const productId = params.get("buy") as ShopProductId | null;
  const session = params.get("session");
  if (!productId || !SHOP_PRODUCTS[productId]?.available) return;

  try {
    const url = new URL(location.href);
    url.searchParams.delete("buy");
    url.searchParams.delete("session");
    history.replaceState({}, "", url.pathname + url.search);
  } catch {
    /* */
  }

  onToast(session ? "Resuming checkout…" : "Starting checkout…");
  await buyProductInApp(productId, onToast);
}
